import {createApiCall} from '../utils';
import {SERVICE_NAMES} from '../constants';
import type {ApiCallOptions} from '../types/api';
import type {
    TransactionVisibilityResponse,
    DocumentVisibilityResponse,
    DocumentVisibilityRequest,
    DocumentVisibilityUpdateResponse
} from './api.d';

/**
 * Document Visibility Service API
 * Provides functions for managing document visibility in transactions.
 * All functions automatically handle authentication and environment detection.
 */
export const documentVisibilityApi = {
    /**
     * Get visibility configuration for all documents in transaction
     * GET /transactions/{transactionId}/visibility
     *
     * @param transactionId - Transaction ID
     * @param options - API call options (auth, timeout, headers)
     * @returns Visibility configurations for all documents
     */
    getTransactionVisibility: (
        transactionId: string,
        options?: ApiCallOptions
    ) => createApiCall<TransactionVisibilityResponse>(
        SERVICE_NAMES.DOCUMENT_VISIBILITY,
        'GET',
        `transactions/${transactionId}/visibility`
    )({...options}),

    /**
     * Get visibility configuration for a specific document
     * GET /transactions/{transactionId}/documents/{documentId}/visibility
     *
     * @param transactionId - Transaction ID
     * @param documentId - Document ID
     * @param options - API call options (auth, timeout, headers)
     * @returns Visibility configuration for the document
     */
    getDocumentVisibility: (
        transactionId: string,
        documentId: string,
        options?: ApiCallOptions
    ) => createApiCall<DocumentVisibilityResponse>(
        SERVICE_NAMES.DOCUMENT_VISIBILITY,
        'GET',
        `transactions/${transactionId}/documents/${documentId}/visibility`
    )({...options}),

    /**
     * Update visibilities for a specific document
     * PUT /transactions/{transaction}/documents/{document}
     *
     * @param transactionId - Transaction ID
     * @param documentId - Document ID
     * @param data - Visibilities array with recipient_id + visible flag per recipient
     * @param options - API call options (auth, timeout, headers)
     * @returns Updated visibility list
     */
    updateDocumentVisibility: (
        transactionId: string,
        documentId: string,
        data: DocumentVisibilityRequest,
        options?: ApiCallOptions
    ) => createApiCall<DocumentVisibilityUpdateResponse>(
        SERVICE_NAMES.DOCUMENT_VISIBILITY,
        'PUT',
        `transactions/${transactionId}/documents/${documentId}`
    )({...options, data})
};
